<script setup>
import {onMounted, onBeforeUnmount, ref, computed, provide, watch} from 'vue';
import axios from 'axios';
import PostContent from "./_postContent.vue";
import ButtonMoreOptions from "../../buttons/buttonMoreOptions.vue";
import tabProgressTrackingStatistics from "./_tabProgressTrackingStatistics.vue";
import tabProgressTrackingReport from "./_tabProgressTrackingReport.vue";

const props = defineProps({
    channelId: {
        type: Number,
        required: true,
    },
    channel: {
        type: Object,
        required: true,
    },
    hideTabBar: {
        type: Boolean,
        required: false,
        default: false,
    },
    hidePostFeed: {
        type: Boolean,
        required: false,
        default: false,
    },
    hideTrackRecord: {
        type: Boolean,
        required: false,
        default: false,
    },
    hideTotalScore: {
        type: Boolean,
        required: false,
        default: false,
    },
    hideScoreboard: {
        type: Boolean,
        required: false,
        default: false,
    },
    hideStatistics: {
        type: Boolean,
        required: false,
        default: false,
    },
    hideChannelMetaText: {
        type: Boolean,
        required: false,
        default: false,
    },
    submitPostButtonLabel: {
        type: String,
        required: false,
        default: _mft('community:channel.newPost.addPost')
    },
    showAddPostTextButton: {
        type: Boolean,
        required: false,
        default: true,
    },
    showOpenChannelButton: {
        type: Boolean,
        required: false,
        default: true,
    },
    showSetPostDateButton: {
        type: Boolean,
        required: false,
        default: true,
    },
    showBackground: {
        type: Boolean,
        required: false,
        default: true,
    }
})

const loadingChannel = ref(false);
const memberships = ref([]);
const posts = ref([]);
const feedPage = ref(0);
const feedLastPage = ref(0);
const loadingMorePosts = ref(false);
const feedSentinel = ref(null);
const postComposer = ref(null);
let feedObserver = null;
const postBeingEdited = ref(null);
const yesterdaysPost = ref(null);
const allowPosting = ref(null);
const trackRecord = ref(null)
const trackRecordDays = ref(null)
const channelMetaText = ref(null)

const showChannel = ref(false);
const activeChannel = ref(null);

const newPostContent = ref(null);
const newPostImage = ref(null);
const filepondUploader = ref(null)
const newPostDate = ref(null);

const commentablePost = ref(null);

const postedToday = ref(false);

const currentUserCanPost = ref(false)

const accountability = ref({});

const showPostComposer = ref(true);
const showPostDateSelector = ref(false);
const newPostState = ref('')

provide('memberships', {memberships});

provide('accountability-data', {accountability, yesterdaysPost, activeChannel});

provide('commentablePost', commentablePost);
provide('activeChannel', activeChannel);

// Children (e.g. the comments list) call this after changing a post
provide('getChannelData', refreshLoadedPosts);

async function getChannelData(channelId, editingPostId = null) {
    loadingChannel.value = true;
    posts.value = []

    try {
        await Promise.all([
            getChannelDetails(channelId, editingPostId),
            loadFeed(channelId),
        ]);
    } catch (error) {
        console.error(error);
    } finally {
        // Otherwise a failed request leaves the full-screen overlay up for good
        loadingChannel.value = false;
    }
}

async function getChannelDetails(channelId, editingPostId = null) {
    let params = {}

    if (editingPostId) {
        params = {
            editingPostId: editingPostId
        }
    }
    const response = await axios.get(route('community.member.channel.details', channelId), {
        params: params
    });

    postBeingEdited.value = response.data.post_being_edited;

    resetAccountability()

    allowPosting.value = response.data.allow_posting;
    yesterdaysPost.value = response.data.yesterdays_post;
    postedToday.value = response.data.posted_today;
    currentUserCanPost.value = response.data.current_user_can_post;
    channelMetaText.value = response.data.meta_text
    trackRecord.value = response.data.track_record
    trackRecordDays.value = response.data.track_record_days

    if (postBeingEdited.value) {
        newPostContent.value = postBeingEdited.value.content
        newPostDate.value = postBeingEdited.value.created_at_user_tz
    }
}

async function fetchFeedPage(channelId, page) {
    const response = await axios.get(route('community.member.channel.feed', channelId), {
        params: {page}
    });

    return response.data;
}

function uniquePosts(postList) {
    const seen = new Set();
    return postList.filter((post) => {
        if (seen.has(post.id)) {
            return false;
        }
        seen.add(post.id);
        return true;
    });
}

async function loadFeed(channelId) {
    if (props.hidePostFeed) {
        return;
    }

    const data = await fetchFeedPage(channelId, 1);

    posts.value = data.posts;
    feedPage.value = data.meta.current_page;
    feedLastPage.value = data.meta.last_page;
}

async function loadMorePosts() {
    if (loadingMorePosts.value || !hasMorePosts.value) {
        return;
    }

    loadingMorePosts.value = true;

    try {
        const data = await fetchFeedPage(activeChannel.value.id, feedPage.value + 1);

        // Offset pagination can repeat a post if new ones were added since the last page
        posts.value = uniquePosts([...posts.value, ...data.posts]);
        feedPage.value = data.meta.current_page;
        feedLastPage.value = data.meta.last_page;
    } finally {
        loadingMorePosts.value = false;
    }

    // observe() reports the current state, so this loads the next page if the sentinel is still in view
    if (feedObserver && feedSentinel.value) {
        feedObserver.unobserve(feedSentinel.value);
        feedObserver.observe(feedSentinel.value);
    }
}

// Re-fetches the pages already loaded, keeping the member's scroll position
async function refreshLoadedPosts(channelId = activeChannel.value.id) {
    if (props.hidePostFeed) {
        return;
    }

    const pageNumbers = Array.from({length: Math.max(feedPage.value, 1)}, (_, i) => i + 1);
    const pages = await Promise.all(pageNumbers.map((page) => fetchFeedPage(channelId, page)));

    posts.value = uniquePosts(pages.flatMap((page) => page.posts));
    feedLastPage.value = pages[pages.length - 1].meta.last_page;
}

const hasMorePosts = computed(() => feedPage.value < feedLastPage.value);

const editPost = async (post) => {
    await getChannelDetails(activeChannel.value.id, post.id);
    postComposer.value?.scrollIntoView({behavior: 'smooth', block: 'start'});
}

const updateFiles = (files) => {
    newPostImage.value = files[0]
}

const removedFile = () => {
    newPostImage.value = null
}

const cancelEditing = () => {
    postBeingEdited.value = null;
    newPostContent.value = null;
    newPostDate.value = null;
    resetAccountability();
}

const deletePost = async (post) => {
    const res = await axios.delete(route('community.member.deletePost', post.id))
    await Promise.all([
        getChannelDetails(activeChannel.value.id),
        refreshLoadedPosts(),
    ]);
}

async function submitPost(channelId) {
    if (newPostValidates.value === false) {
        return;
    }

    newPostState.value = 'waiting';

    let formData = new FormData();
    formData.append('content', newPostContent.value);
    if (newPostDate.value) {
        formData.append('date', newPostDate.value);
    }
    formData.append('accountability', JSON.stringify(accountability.value));

    if (newPostImage.value) {
        formData.append('image', newPostImage.value.file);
    }

    if (postBeingEdited.value) {
        formData.append('editingPostId', postBeingEdited.value.id);
    }

    try {
        const response = await axios.post(route('community.member.addPost', channelId), formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        if (response.data.success === false) {
            throw new Error('Post submission failed');
        }

        newPostState.value = 'success';

        if (postBeingEdited.value) {
            await Promise.all([
                getChannelDetails(channelId, postBeingEdited.value.id),
                refreshLoadedPosts(channelId),
            ]);
            if (!activeChannel.value.progress_tracking_enabled) {
                cancelEditing()
            }
        } else {
            // The new post lands on top and shifts every page, so start over from the first one
            await Promise.all([
                getChannelDetails(channelId),
                loadFeed(channelId),
            ]);
            newPostContent.value = null;
            newPostDate.value = null;
        }

        resetAccountability();
    } catch (error) {
        newPostState.value = 'error';
        console.error(error);
    }

    filepondUploader.value.clearFiles()
}

async function highFivePost(post) {
    const toggleHighFive = () => {
        post.likes += post.is_high_fived ? -1 : 1;
        post.is_high_fived = !post.is_high_fived;
    }

    toggleHighFive();

    try {
        await axios.post(route('community.member.highFivePost', post.id));
    } catch (error) {
        toggleHighFive();
        console.error(error);
    }
}

async function flagPost(post) {
    post.is_flagged = !post.is_flagged;

    try {
        await axios.post(route('community.member.flagPost', post.id));
    } catch (error) {
        post.is_flagged = !post.is_flagged;
        console.error(error);
    }
}

const resetAccountability = () => {
    if (props.channel.accountability_version === 1) {
        accountability.value.question1 = null
        accountability.value.question2 = null
        accountability.value.question3 = null
        accountability.value.yesterdaysQuestion1 = null
        accountability.value.yesterdaysQuestion2 = null
        accountability.value.yesterdaysQuestion3 = null
    }

    if (props.channel.accountability_version === 2) {
        accountability.value.questions = []

        if (postBeingEdited.value && postBeingEdited.value.post_accountability_questions_actions) {
            postBeingEdited.value.post_accountability_questions_actions.questions.forEach((question, i) => {
                let data = {
                    uuid: question.uuid,
                    index: question.index,
                    question: question.question,
                    enabled: question.enabled,
                    type: question.type,
                    role: question.role ? question.role : null,
                    answer: question.answer,
                    defaultAnswer: question.defaultAnswer,
                    reflection: question.reflection,
                    metadata: question.metadata,
                }
                accountability.value.questions.push(data)
            })
        } else {
            if (props.channel.accountability_enabled || props.channel.progress_tracking_enabled) {
                props.channel.accountability_questions_actions?.questions.forEach((question, i) => {
                    let data = {
                        uuid: question.uuid,
                        index: question.index,
                        question: question.question,
                        enabled: question.enabled,
                        type: question.type,
                        role: question.role ? question.role : null,
                        defaultAnswer: question.defaultAnswer,
                        answer: null,
                        reflection: null,
                        metadata: question.metadata,
                    }
                    accountability.value.questions.push(data)
                })
            }
        }
    }
}

function prepareComment(post) {
    if (commentablePost.value === post.id) {
        commentablePost.value = null;
        return;
    }
    commentablePost.value = post.id;
}

const showTabbedUi = computed(() => {
    if (props.hideTabBar) {
        return false;
    }
    return activeChannel.value.progress_tracking_enabled || channelMetaText.value;
})

const canPost = computed(() => {
    if (activeChannel.value.accountability_enabled) {
        return !postedToday.value;
    }

    if (activeChannel.value.archived === true) {
        return false;
    }

    return currentUserCanPost.value;
})

const newPostValidates = computed(() => {
    if (!activeChannel.value.accountability_enabled &&
        !activeChannel.value.progress_tracking_enabled &&
        (newPostContent.value === null || newPostContent.value.length === 0)
    ) {
        return false
    }

    if (!activeChannel.value.accountability_enabled && !activeChannel.value.progress_tracking_enabled) {
        return true
    }

    if (props.channel.accountability_version === 1) {
        return accountabilityV1Validates.value;
    }

    if (props.channel.accountability_version === 2) {
        return accountabilityV2Validates.value
    }

    return true
});

const accountabilityV1Validates = computed(() => {
    if (
        activeChannel.value.accountability_questions_actions.question1 !== null
        && (!accountability.value.question1 || accountability.value.question1.length === 0)
    ) {
        return false
    }

    if (
        activeChannel.value.accountability_questions_actions.question2 !== null
        && (!accountability.value.question2 || accountability.value.question2.length === 0)
    ) {
        return false
    }

    if (
        activeChannel.value.accountability_questions_actions.question3 !== null
        && (!accountability.value.question3 || accountability.value.question3.length === 0)
    ) {
        return false
    }

    return true
})

const accountabilityV2Validates = computed(() => {
    let validates = true
    // Validation is a mess I will take care of later.
    // Or maybe I will just leave it as it is, since post editing is now a thing.
    return validates
})

const postComposerVisible = computed(() => {
    return showPostComposer.value || (postBeingEdited.value && newPostContent.value?.length > 0)
})

watch(feedSentinel, (element, previousElement) => {
    if (previousElement) {
        feedObserver?.unobserve(previousElement);
    }
    if (element) {
        feedObserver?.observe(element);
    }
});

onMounted(() => {
    feedObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
            loadMorePosts();
        }
    }, {rootMargin: '400px'});

    getChannelData(props.channelId);
    activeChannel.value = props.channel;
    showChannel.value = true;
    if (activeChannel.value.progress_tracking_enabled && newPostContent) {
        showPostComposer.value = false;
    }
});

onBeforeUnmount(() => {
    feedObserver?.disconnect();
});

</script>

<template>
    <div
        v-if="showChannel"
        class="relative"
    >
        <div
            v-if="loadingChannel"
            class="absolute inset-0 flex items-center justify-center h-screen bg-white/90"
        >
            <mf-spinner-medium class="w-24 h-12"/>
        </div>
        <div class="-mt-4 lg:-mt-6">
            <component
                :is="showTabbedUi ? 'ui-tabbed' : 'div'"
                :track-state="false"
            >
                <component
                    title="Post"
                    :is="showTabbedUi ? 'ui-tabbed-tab' : 'div'"
                    value="posts"
                    :active="true"
                >
                    <div
                        v-if="canPost && allowPosting"
                        ref="postComposer"
                        class="px-4 pt-4 -mx-4 border-b lg:-mx-6 lg:px-6 lg:pt-6"
                        :class="props.showBackground ? 'bg-gray-100 border-t' : ''"
                    >
                        <div
                            class="flex flex-col"
                            :class="activeChannel.progress_tracking_enabled ? 'flex-col-reverse' : ''"
                        >
                            <div
                                v-if="postComposerVisible"
                                class="mb-2 md:mb-0 md:grid md:grid-cols-4 md:gap-4"
                            >
                                <div class="md:col-span-3">
                                    <input-textarea
                                        :placeholder="_mft('community:channel.newPost.placeholder')"
                                        v-model:content="newPostContent"
                                        class="!mb-2"
                                        input-height-class="h-16"
                                    ></input-textarea>
                                </div>

                                <div
                                    class="md:col-span-1 flex items-center justify-stretch md:-mt-2 md:h-full"
                                    :class="newPostImage ? 'mb-2' : ''"
                                >
                                    <div class="w-full border-gray-300 rounded-wl border-2 border-dashed">
                                        <filepond-upload
                                            class="rounded-wl overflow-hidden"
                                            :class="newPostImage ? '' : 'h-12'"
                                            ref="filepondUploader"
                                            :showPreview="true"
                                            accepted-file-types="image/png,image/jpeg,image/jpg,image/gif"
                                            :required="false"
                                            max-file-size="8MB"
                                            :label-idle="_mft('community:channel.newPost.addImage')"
                                            @updatefiles="updateFiles($event)"
                                            @removedfile="removedFile()"
                                        ></filepond-upload>
                                    </div>
                                </div>

                            </div>

                            <div v-if="activeChannel.accountability_enabled || activeChannel.progress_tracking_enabled">
                                <accountability-post-data
                                    v-if="activeChannel.accountability_version === 1"
                                ></accountability-post-data>

                                <accountability-v2-post-data
                                    v-if="activeChannel.accountability_version === 2"
                                    :track-record="trackRecord"
                                    :track-record-days="trackRecordDays"
                                    :hide-track-record="props.hideTrackRecord"
                                    :hide-total-score="props.hideTotalScore"
                                ></accountability-v2-post-data>
                            </div>

                        </div>

                        <div class="relative md:static flex justify-end items-start -mt-1 space-x-2">
                            <div
                                v-if="props.showSetPostDateButton && activeChannel.progress_tracking_enabled && activeChannel.accountability_version === 2"
                                class="md:relative flex items-start"
                            >
                                <button-secondary
                                    v-if="! showPostDateSelector"
                                    size="sm"
                                    class="mb-3 text-center"
                                    v-on:click="showPostDateSelector = true"
                                >
                                    <i v-if="newPostDate" class="fa-regular fa-calendar mr-1 text-gray-400"></i>
                                    {{ newPostDate ? newPostDate.split(' ')[0] : _mft('community:channel.setPostDate') }}
                                </button-secondary>
                                <div
                                    v-if="showPostDateSelector"
                                    class="bg-white rounded-lg shadow-lg p-4 mb-2"
                                >
                                    <div class="flex justify-between -mt-2 mb-2">
                                        <input-label
                                            :label="_mft('community:channel.setPostDate')"
                                            class="mb-1 text-black"
                                        />
                                        <button-transparent
                                            size="sm"
                                            @click="showPostDateSelector = false"
                                        >
                                            <i class="fa-solid fa-xmark text-black"></i>
                                        </button-transparent>
                                    </div>
                                    <input-datepicker
                                        :date="newPostDate"
                                        :min-date="null"
                                        :max-date="new Date()"
                                        :show-time="false"
                                        format="yyyy-MM-dd"
                                        @date-time-selected-formatted="(event) => {newPostDate = event; showPostDateSelector = false;}"
                                    />
                                </div>
                            </div>

                            <button-secondary
                                size="sm"
                                v-if="props.showAddPostTextButton && activeChannel.progress_tracking_enabled && ! newPostContent && ! showPostComposer"
                                class="mb-3 text-center"
                                v-on:click="showPostComposer = true"
                            >
                                {{ _mft('community:channel.addPostText') }}
                            </button-secondary>
                            <button-secondary
                                size="sm"
                                v-if="postBeingEdited && ! activeChannel.progress_tracking_enabled"
                                class="mb-3 text-center"
                                v-on:click="cancelEditing"
                            >
                                {{ _mft('shared:cancel') }}
                            </button-secondary>
                            <button-secondary
                                size="sm"
                                :style-type="newPostValidates ? 'primary' : 'disabled'"
                                :state="newPostState"
                                class="mb-3 text-center"
                                :disabled="! newPostValidates"
                                v-on:click="submitPost(activeChannel.id)"
                            >
                                {{
                                    activeChannel.progress_tracking_enabled && postBeingEdited ? _mft('community:channel.newPost.update') : props.submitPostButtonLabel
                                }}
                            </button-secondary>
                        </div>
                    </div>
                    <div v-if="! hidePostFeed">
                        <p
                            v-if="posts.length === 0 && ! loadingChannel"
                            class="py-8 text-center text-gray-400"
                        >
                            {{ _mft('community:channel.noPosts') }}
                        </p>

                        <!-- updated_at in the key remounts an edited post, since some children copy their props into local state -->
                        <div
                            v-for="post in posts"
                            :key="`${post.id}-${post.updated_at}`"
                        >
                            <div class="flex py-4 space-x-2">
                                <div class="w-6 shrink-0 md:w-10">
                                    <img
                                        class="object-cover object-center w-6 h-6 rounded-full md:h-10 md:w-10"
                                        :src="post.user.profile_img"
                                        alt="avatar"
                                    >
                                </div>
                                <div class="w-full">
                                    <div class="md:flex items-center justify-between h-12 -translate-y-px md:h-10 relative z-40 mb-2">
                                        <div>
                                            <span class="font-semibold">{{ post.user.name }}</span>
                                            <ul class="text-xs flex flex-wrap space-x-1">
                                                <li
                                                    v-for="badge in post.user.badges"
                                                    class="block bg-wlPrimary text-wlPrimaryContrast rounded-wl cursor-default text-xxs px-1.5 py-px"
                                                >
                                                    {{ badge.name }}
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="flex items-center flex-row-reverse justify-end space-x-0 md:flex-row">
                                            <span class="mx-2">
                                                <button-more-options
                                                    v-if="post.owned_by_current_user && ! channel.accountability_enabled && postBeingEdited?.id !== post.id"
                                                    size="sm"
                                                    alignment="center"
                                                    :cancel-on-outside-click="false"
                                                >
                                                    <button-more-options-option
                                                        @click="editPost(post)"
                                                    >
                                                        {{ _mft('shared:edit') }}
                                                    </button-more-options-option>
                                                    <button-more-options-option-with-confirmation
                                                        :confirm-button-label="_mft('shared:action.delete')"
                                                        @confirmed="deletePost(post)"
                                                    >
                                                        {{ _mft('shared:action.delete') }}
                                                    </button-more-options-option-with-confirmation>
                                                </button-more-options>
                                            </span>
                                            <span class="relative">
                                                <span class="text-xs text-gray-400">{{ post.created_at_user_tz }}</span>
                                                <span
                                                    v-if="post.created_at !== post.updated_at"
                                                    class="text-xxs bg-gray-200 text-gray-600 cursor-default px-2 py-0.5 rounded-full text-right absolute top-full right-0"
                                                >
                                                    {{ _mft('shared:edited') }}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                    <PostContent
                                        :post="post"
                                    />

                                    <div
                                        v-if="channel.accountability_enabled || channel.progress_tracking_enabled"
                                        class="grid gap-4 p-4 mb-4 -ml-4 border rounded-wl md:grid-cols-2 md:gap-8"
                                    >
                                        <div v-if="channel.accountability_enabled">
                                            <h5 class="text-sm mb-1">
                                                {{ _mft('community:channel.yesterdaysReflection.title') }}
                                            </h5>

                                            <div v-if="activeChannel.accountability_version === 2">
                                                <div
                                                    v-for="(question, i) in post.reflection_display?.questions"
                                                    :key="i"
                                                >
                                                    <community-yesterday-reflection
                                                        :active-channel="activeChannel"
                                                        :enabled="true"

                                                        :question="question.question"
                                                        :type="question.type"
                                                        :answer="question.answer"

                                                        :previous-question="question.reflection"
                                                    ></community-yesterday-reflection>
                                                </div>
                                            </div>

                                            <div v-if="activeChannel.accountability_version === 1">
                                                <community-yesterday-reflection
                                                    :active-channel="activeChannel"
                                                    :enabled="activeChannel.accountability_questions_actions.question1Enabled"

                                                    :question="activeChannel.accountability_questions_actions.question1"
                                                    :answer="post.previous_post_accountability_questions_actions?.question1"

                                                    :previous-question="post.post_accountability_questions_actions.yesterdaysQuestion1"
                                                ></community-yesterday-reflection>

                                                <community-yesterday-reflection
                                                    :active-channel="activeChannel"
                                                    :enabled="activeChannel.accountability_questions_actions.question2Enabled"

                                                    :question="activeChannel.accountability_questions_actions.question2"
                                                    :answer="post.previous_post_accountability_questions_actions?.question2"

                                                    :previous-question="post.post_accountability_questions_actions.yesterdaysQuestion2"
                                                ></community-yesterday-reflection>

                                                <community-yesterday-reflection
                                                    :active-channel="activeChannel"
                                                    :enabled="activeChannel.accountability_questions_actions.question3Enabled"

                                                    :question="activeChannel.accountability_questions_actions.question3"
                                                    :answer="post.previous_post_accountability_questions_actions?.question3"

                                                    :previous-question="post.post_accountability_questions_actions.yesterdaysQuestion3"
                                                ></community-yesterday-reflection>
                                            </div>

                                        </div>

                                        <div>
                                            <h5 class="text-sm mb-1">
                                                {{ _mft('community:channel.dailyActions.title') }}
                                            </h5>

                                            <div v-if="activeChannel.accountability_version === 2">
                                                <div
                                                    v-for="(question, i) in post.post_accountability_questions_actions?.questions"
                                                    :key="i"
                                                >
                                                    <community-todays-post-action
                                                        :enabled="post.post_accountability_questions_actions.questions[i].enabled"
                                                        :question="post.post_accountability_questions_actions.questions[i].question"
                                                        :answer="post.post_accountability_questions_actions.questions[i].answer"
                                                        :type="post.post_accountability_questions_actions.questions[i].type"
                                                        :role="post.post_accountability_questions_actions.questions[i].role"
                                                    ></community-todays-post-action>
                                                </div>
                                            </div>

                                            <div v-if="activeChannel.accountability_version === 1">
                                                <community-todays-post-action
                                                    :enabled="activeChannel.accountability_questions_actions.question1Enabled"
                                                    :question="activeChannel.accountability_questions_actions.question1"
                                                    :answer="post.post_accountability_questions_actions.question1"
                                                ></community-todays-post-action>

                                                <community-todays-post-action
                                                    :enabled="activeChannel.accountability_questions_actions.question2Enabled"
                                                    :question="activeChannel.accountability_questions_actions.question2"
                                                    :answer="post.post_accountability_questions_actions.question2"
                                                ></community-todays-post-action>

                                                <community-todays-post-action
                                                    :enabled="activeChannel.accountability_questions_actions.question3Enabled"
                                                    :question="activeChannel.accountability_questions_actions.question3"
                                                    :answer="post.post_accountability_questions_actions.question3"
                                                ></community-todays-post-action>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="flex mt-4 space-x-1 md:space-x-2">
                                        <button-base
                                            :style-type="commentablePost === post.id ? 'secondary' : 'base'"
                                            size="sm"
                                            v-on:click="prepareComment(post)"
                                        >
                                            <i class="mr-1 fa-regular fa-comment"></i>
                                            {{ _mft('community:post.comment') }}
                                        </button-base>

                                        <button-base
                                            v-if="! post.is_high_fived"
                                            v-on:click="highFivePost(post)"
                                            size="sm"
                                        >
                                            <i class="mr-1 fa-regular fa-hand-wave"></i>
                                            {{ _mft('community:post.highFive') }}
                                        </button-base>
                                        <button-base
                                            v-else
                                            style-type="transparent"
                                            size="sm"
                                            class="text-myflowGreen"
                                            v-on:click="highFivePost(post)"
                                        >
                                            <i class="mr-1 fa-regular fa-hand-wave"></i>
                                            {{ _mft('community:post.highFived') }}
                                        </button-base>

                                        <button-base
                                            v-if="! post.is_flagged"
                                            v-on:click="flagPost(post)"
                                            size="sm"
                                            class="text-myflowRed-600"
                                            classes="!border-myflowRed-600"
                                        >
                                            <i class="mr-1 fa-regular fa-flag"></i>
                                            {{ _mft('community:post.flag') }}
                                        </button-base>
                                        <button-base
                                            v-else
                                            v-on:click="flagPost(post)"
                                            size="sm"
                                            style-type="transparent"
                                            class="text-myflowRed-600"
                                            classes="!border-myflowRed-600"
                                        >
                                            <i class="mr-1 fa-solid fa-flag"></i>
                                            {{ _mft('community:post.flagged') }}
                                        </button-base>
                                    </div>
                                </div>

                            </div>

                            <community-channel-comments-list
                                :post-data="post"
                            ></community-channel-comments-list>
                        </div>

                        <div
                            v-if="hasMorePosts"
                            ref="feedSentinel"
                            class="flex justify-center py-6"
                        >
                            <mf-spinner-medium
                                v-if="loadingMorePosts"
                                class="w-24 h-12"
                            />
                            <button-base
                                v-else
                                size="sm"
                                @click="loadMorePosts"
                            >
                                {{ _mft('community:channel.loadMorePosts') }}
                            </button-base>
                        </div>
                    </div>
                </component>

                <component
                    v-if="activeChannel.progress_tracking_enabled && ! props.hideScoreboard"
                    :is="showTabbedUi ? 'ui-tabbed-tab' : 'div'"
                    title="Scoreboard"
                    value="scoreboard"
                >
                    <h2 class="text-center mb-2">Scoreboard</h2>
                    <tab-progress-tracking-statistics
                        :channel-id="activeChannel.id"
                    ></tab-progress-tracking-statistics>
                </component>

                <component
                    v-if="channelMetaText && ! props.hideChannelMetaText"
                    :is="showTabbedUi ? 'ui-tabbed-tab' : 'div'"
                    title="Information"
                    value="information"
                >
                    <div
                        v-if="channelMetaText"
                        v-html="channelMetaText"
                        class="-mt-4 lg:-mt-6 pt-4 lg:pt-6 border-b pb-8 mb-8 -mx-4 lg:-mx-6 px-4 lg:px-6 bg-gray-100"
                    ></div>
                </component>

                <component
                    v-if="activeChannel.progress_tracking_enabled && trackRecord && ! hideStatistics"
                    :is="showTabbedUi ? 'ui-tabbed-tab' : 'div'"
                    value="progress-tracking-report"
                    title="Statistics"
                    :active="false"
                >
                    <div>
                        <tab-progress-tracking-report
                            :channel-id="activeChannel.id"
                        ></tab-progress-tracking-report>
                    </div>
                </component>
            </component>
        </div>
    </div>
</template>
<style scoped>
/*
:deep(.filepond--root) {
    margin-bottom: 0;
    height: 30px;
    min-height: 30px;
    max-height: 30px;
}
:deep(.filepond--root .filepond--drop-label) {
    min-height: 30px !important;
}
/
 */
</style>
